import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAboutData, useContactData } from '../lib/cmsStore';
import { ContactModal } from './ContactModal';
const lineReveal = {
  hidden: {
    y: '100%'
  },
  visible: (i: number) => ({
    y: '0%',
    transition: {
      duration: 0.9,
      delay: 0.3 + i * 0.15,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay: 0.9 + i * 0.12,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
export function HeroSection() {
  const {
    data,
    isLoading
  } = useAboutData();
  // ISSUE 1 FIX: Don't show any content until CMS data has loaded.
  // data is null while loading (no more default seed data flashing).
  const availabilityText = data?.availabilityText || '';
  const socialLinks = data?.socialLinks ? data.socialLinks.filter(link => link.visible) : [];
  const roles = data?.roles?.length ? data.roles : [];
  const description = data?.heroTitle || '';
  // Contact modal
  const [showContactModal, setShowContactModal] = useState(false);
  const {
    data: contactData
  } = useContactData();
  const contactEmail = contactData?.email || 'your@email.com';
  // Typewriter effect
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cursorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!description) return;
    setDisplayedText('');
    setShowCursor(true);
    let charIndex = 0;
    // Clear any previous timers
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (cursorTimeoutRef.current) clearTimeout(cursorTimeoutRef.current);
    // Delay start to sync with fadeUp animation
    const startDelay = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        charIndex++;
        setDisplayedText(description.slice(0, charIndex));
        if (charIndex >= description.length) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          cursorTimeoutRef.current = setTimeout(() => setShowCursor(false), 2000);
        }
      }, 30);
    }, 1200);
    return () => {
      clearTimeout(startDelay);
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (cursorTimeoutRef.current) clearTimeout(cursorTimeoutRef.current);
    };
  }, [description]);
  // Show nothing while CMS data is loading to prevent stale default data flash
  if (isLoading || !data) {
    return <section id="hero" className="relative min-h-screen flex flex-col justify-center px-6 md:px-12 overflow-hidden" data-id="element-195">
        <div className="flex items-center gap-3" data-id="element-196">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" data-id="element-197" />
          <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest" data-id="element-198">
            Loading...
          </span>
        </div>
      </section>;
  }
  return <section id="hero" className="relative min-h-screen flex flex-col justify-center px-6 md:px-12 overflow-hidden" data-id="element-199">
      {/* Background accent circle */}
      <div className="absolute top-1/4 right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/5 blur-[100px]" data-id="element-200" />

      <div className="relative z-10" data-id="element-201">
        {/* Status line */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1,
        ease: [0.22, 1, 0.36, 1]
      }} className="flex items-center gap-3 mb-8" data-id="element-202">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse" data-id="element-203" />
          <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest" data-id="element-204">
            {availabilityText}
          </span>
        </motion.div>

        {/* Main title — first role as one line, subsequent roles split by word */}
        {roles.flatMap((role, roleIndex) => {
        const words = roleIndex === 0 ? [role] : role.split(' ');
        return words.map((word, wordIndex) => {
          const lineIndex = roles.slice(0, roleIndex).reduce((acc, r) => acc + (roles.indexOf(r) === 0 ? 1 : r.split(' ').length), 0) + wordIndex;
          return <div key={`${roleIndex}-${wordIndex}`} className="overflow-hidden" data-id="element-205">
                <motion.h1 custom={lineIndex} variants={lineReveal} initial="hidden" animate="visible" className={`font-display font-bold leading-[0.85] tracking-tighter text-[clamp(2.4rem,9.6vw,8rem)] ${roleIndex >= 1 ? 'text-gradient' : ''}`} data-id="element-206">
                  {word.toUpperCase()}
                </motion.h1>
              </div>;
        });
      })}

        {/* Bottom info */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mt-16 gap-8" data-id="element-207">
          <motion.div custom={0} variants={fadeUp} initial="hidden" animate="visible" className="relative flex items-start gap-5 min-h-[40px]" data-id="element-208">
            {/* Avatar — absolutely positioned so it never moves with text */}
            <div className="absolute left-0 top-0 shrink-0" data-id="element-209">
              <button onClick={() => setShowContactModal(true)} className="relative cursor-pointer block" aria-label="Send me a message" data-id="element-210">
                <img src="https://cdn.magicpatterns.com/uploads/25M748JZJE89fva4j9puso/s1nka1.png" alt="Avatar" className="w-[34px] h-[34px] md:w-[40px] md:h-[40px] rounded-full object-cover" data-id="element-211" />
              </button>
            </div>
            {/* Text — offset by avatar width so it never overlaps */}
            <span className="font-mono text-xs text-muted-foreground leading-relaxed max-w-xl min-h-[4.5em] block pl-[46px] md:pl-[52px]" data-id="element-212">
              {displayedText}
              <span className={`inline-block w-[2px] h-[1em] bg-primary ml-0.5 align-text-bottom ${showCursor ? 'animate-pulse' : 'opacity-0'}`} data-id="element-213" />
            </span>
          </motion.div>

          <motion.div custom={1} variants={fadeUp} initial="hidden" animate="visible" className="flex items-center gap-8" data-id="element-214">
            {socialLinks.map(link => <a key={link.platform} href={link.url} className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors duration-300 border-b border-transparent hover:border-primary pb-1" data-id="element-215">
                {link.platform}
              </a>)}
          </motion.div>
        </div>
      </div>

      {/* Full-page contact modal */}
      <AnimatePresence data-id="element-216">
        {showContactModal && <ContactModal onClose={() => setShowContactModal(false)} recipientEmail={contactEmail} data-id="element-217" />}
      </AnimatePresence>
    </section>;
}